import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import PageMeta from '../components/PageMeta'
import GalleryLightbox from '../components/GalleryLightbox'
import { PAGES_DATA, COMPANY_INFO, COMMON_FEATURES, SUBPAGE_DESIGN_DATA } from '../data/pagesData'

function BeforeAfterSlider({ beforeImage, afterImage, alt = "Før og efter" }) {
  const [sliderPos, setSliderPos] = useState(50)

  return (
    <div className="relative w-full h-full min-h-[350px] lg:min-h-full overflow-hidden select-none group rounded-2xl">
      {/* Before Image (Right side underneath) */}
      <img
        src={beforeImage}
        alt={`${alt} - Før`}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* After Image Overlay (Left side clipped) */}
      <div
        className="absolute inset-0 z-10"
        style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
      >
        <img
          src={afterImage}
          alt={`${alt} - Efter`}
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Divider line and handle */}
      <div
        className="absolute top-0 bottom-0 z-20 w-1 bg-white shadow-xl cursor-ew-resize -ml-0.5 pointer-events-none"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-900 text-xs font-bold border border-slate-300">
          <i className="fa-solid fa-arrows-left-right text-xs" aria-hidden="true" />
        </div>
      </div>

      {/* Hidden range input for interactive touch & drag */}
      <input
        type="range"
        min="0"
        max="100"
        value={sliderPos}
        onChange={(e) => setSliderPos(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30"
        aria-label="Før og efter sammenligning slider"
      />
    </div>
  )
}

export default function ServiceDetailPage({ forcedSlug }) {
  const { slug: routeSlug } = useParams()
  const pageSlug = forcedSlug || routeSlug || ''
  const cleanSlug = pageSlug.replace(/^\/|\/$/g, '')

  const [cmsPage, setCmsPage] = useState(null)

  useEffect(() => {
    // Fetch CMS page entry if available
    fetch(`https://api.dev.auxo.dk/public/v2/content/pages`)
      .then(res => res.json())
      .then(data => {
        if (data && data.items && data.items.length > 0) {
          const match = data.items.find(item => item.page_slug === cleanSlug)
          if (match) {
            setCmsPage(match)
          }
        }
      })
      .catch(err => console.error('Error fetching CMS page:', err))
  }, [cleanSlug])

  const staticPage = PAGES_DATA[cleanSlug] || PAGES_DATA['service']

  const pageTitle = cmsPage?.meta_title || cmsPage?.title || staticPage.title
  const pageDesc = cmsPage?.meta_description || staticPage.metaDescription
  const mainHeading = cmsPage?.title || staticPage.mainHeading || staticPage.heroTitle

  // Image helper for Cloudflare resize
  const getOptimizedImg = (url, width = 1000) => {
    if (!url) return ''
    if (url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`)
    }
    return url
  }

  const heroImageSrc = (cleanSlug === 'service' ? "https://assets.auxodev.dk/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/imgi-14-tilfaeldigt-scaled.jpg" : null) || (cleanSlug === 'udskiftning-af-tandrem-og-taktkaede' ? "https://assets.auxodev.dk/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/imgi-14-tandrem-taktkaede-scaled.jpg" : null) || staticPage.heroImage || "https://assets.auxodev.dk/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/524965098-17887908123318194-4270870576614691254-n-1000x1000.jpg"

  return (
    <div>
      <PageMeta title={pageTitle} description={pageDesc} />

      {/* Top Hero Section: Image Left, Text Right */}
      <section className="bg-white text-slate-900 py-12 lg:py-[100px] border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            
            {/* Left: Image matching page content (stretches to match text height on desktop) */}
            <div className="relative w-full h-full min-h-[320px] lg:min-h-0 rounded-2xl overflow-hidden shadow-md border border-slate-200 bg-slate-100 flex">
              {cleanSlug === 'walnut-blaster' ? (
                <BeforeAfterSlider
                  beforeImage={getOptimizedImg("https://assets.auxodev.dk/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/foer.jpg", 1000)}
                  afterImage={getOptimizedImg("https://assets.auxodev.dk/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/efter.jpg", 1000)}
                  alt="Walnut blaster før og efter"
                />
              ) : (
                <img
                  src={getOptimizedImg(heroImageSrc, 1000)}
                  alt={mainHeading}
                  className="w-full h-full object-cover object-center lg:absolute lg:inset-0"
                />
              )}
            </div>

            {/* Right: Text content */}
            {cleanSlug === 'a-c-service' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#046BB4]">
                  Din bil fortjener den bedste pleje
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-[1.15]">
                  A/C Service i Varde – Professionel aircondition service hos Sakskær Auto
                </h1>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde tilbyder vi professionel A/C-service til alle typer biler – både benzin, diesel, hybrid og elbiler. Et velfungerende airconditionanlæg er ikke kun en luksus om sommeren, men en vigtig del af bilens komfort og sikkerhed året rundt.
                  </p>
                  <p>
                    Vi anbefaler, at du får foretaget et airconditionservice minimum hvert andet år for at sikre optimal drift, forlænge levetiden på systemet og undgå dyre reparationer.
                  </p>
                </div>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  FORDELE VED REGELMÆSSIG A/C-SERVICE:
                </h2>

                <ul className="list-disc list-outside pl-5 space-y-2 text-slate-700 text-sm sm:text-base">
                  <li>Bedre komfort og indeklima</li>
                  <li>Et rent og effektivt A/C-system giver dig kølig og frisk luft – uden dårlig lugt, bakterier eller skimmelsvamp.</li>
                </ul>

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'gearkasse-service' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#046BB4]">
                  DIN BIL FORTJENER DEN BEDSTE PLEJE
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-[1.15]">
                  GEARKASSE SERVICE HOS SAKSKÆR AUTO I VARDE – VIGTIGT FOR BILENS HOLDBARHED
                </h1>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde anbefaler vi gearkasse service – også på biler, hvor producenten ikke har fastsat officielle intervaller. Mange bilister overser gearkassen, men den er en af bilens mest belastede og vigtige komponenter, som fortjener regelmæssig pleje.
                  </p>
                </div>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  HVORFOR ER GEARKASSE SERVICE VIGTIGT – SELV UDEN FASTE INTERVALLER?
                </h2>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Selvom mange moderne biler ikke har officielle anbefalinger for gearkasse service, kan gammel eller forurenet gearolie føre til øget slid og funktionsfejl. Gearkassen indeholder mange præcisionsdele, som kræver korrekt smøring for at fungere optimalt og undgå overophedning.
                  </p>
                </div>

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'daek-og-faelge' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#046BB4]">
                  DIN BIL FORTJENER DEN BEDSTE PLEJE
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-[1.15]">
                  ALT I DÆK OG FÆLGE HOS SAKSKÆR AUTO I VARDE
                </h1>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde tilbyder vi alt inden for dæk og fælge til konkurrencedygtige priser. Uanset om du har brug for sommerdæk, vinterdæk, helårsdæk eller fælge til din personbil eller varevogn, står vi klar med professionel vejledning og service.
                  </p>
                  <p>
                    Vi sikrer, at dine dæk og fælge er monteret korrekt og fungerer optimalt – så du får maksimal sikkerhed og køreglæde.
                  </p>
                </div>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  VORES DÆKSPECIALIST TILBYDER:
                </h2>

                <ul className="list-disc list-outside pl-5 space-y-2 text-slate-700 text-sm sm:text-base font-normal">
                  <li>Montering af dæk og fælge</li>
                  <li>Afbalancering for optimal kørestabilitet</li>
                  <li>Udskiftning og service af TPMS-ventiler (dæktryksovervågning)</li>
                  <li>Rådgivning om de bedste dæk og fælge til din bil og kørselsbehov</li>
                  <li>Konkurrencedygtige priser uden at gå på kompromis med kvaliteten</li>
                </ul>

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'gearkasse-skylning' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#046BB4]">
                  DIN BIL FORTJENER DEN BEDSTE PLEJE
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-[1.15]">
                  GEARKASSE SKYLNING HOS SAKSKÆR AUTO I VARDE – EFFEKTIV RENS AF AUTOMATGEARKASSE
                </h1>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde tilbyder vi professionel gearkasse skylning til alle typer automatgearkasser. En gearkasse skylning er en dybdegående service, hvor 100% af den gamle og forurenede gearolie udskiftes med frisk, fabriksgodkendt kvalitetsoile.
                  </p>
                  <p>
                    Ved et almindeligt olieskift bliver op mod 40-50% af den gamle olie tilbage i converteren og oliekøleren. Vores specialmaskine skylles hele systemet igennem under kontrolleret tryk, så aflejringer, sod og slidpartikler renses helt ud – hvilket forlænger gearkassens levetid og sikrer silkebløde gearskift.
                  </p>
                </div>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  FORDELE VED AUTOMATGEARKASSE SKYLNING:
                </h2>

                <ul className="list-disc list-outside pl-5 space-y-2 text-slate-700 text-sm sm:text-base font-normal">
                  <li>100% udskiftning af den forurenede gearolie samt rens af ventilhus og converter</li>
                  <li>Afhjælper ru, hårde eller tøvende gearskift og ubehagelige ryk</li>
                  <li>Forebygger dyre reparationer og forlænger automatgearkassens levetid markant</li>
                  <li>Optimerer brændstoføkonomien og sikrer maksimal kørekomfort</li>
                </ul>

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'kundebil' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#046BB4]">
                  DIN BIL FORTJENER DEN BEDSTE PLEJE
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-[1.15]">
                  GRATIS LÅNEBIL HOS SAKSKÆR AUTO I VARDE – SPØRG VED BOOKING
                </h1>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde ved vi, hvor vigtigt det er at være mobil, selv når din bil er på værksted. Derfor tilbyder vi altid mulighed for gratis lånebil, når du får service eller reparation hos os.
                  </p>
                  <p>
                    Når du booker tid til din bil, så husk at spørge efter en gratis lånebil – så sørger vi for, at du kan komme rundt, mens vi passer på din bil.
                  </p>
                </div>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  HVORFOR VÆLGE SAKSKÆR AUTO?
                </h2>

                <ul className="list-disc list-outside pl-5 space-y-2 text-slate-700 text-sm sm:text-base font-normal">
                  <li>Professionelt autoværksted i Varde med mange års erfaring</li>
                  <li>Gratis lånebil ved service og reparation – for din bekvemmelighed</li>
                  <li>Hurtig og effektiv service, så du ikke er uden bil længere end højst nødvendigt</li>
                  <li>Professionel rådgivning og kvalitetsarbejde med det nyeste udstyr</li>
                </ul>

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'reparation' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#046BB4]">
                  Din bil fortjener den bedste pleje
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-[1.15]">
                  Sakskær Auto – Dit autoværksted i Varde
                </h1>

                <div className="space-y-3 text-slate-700 text-sm sm:text-base font-normal leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde får du et autoværksted med fokus på professionalisme, godt håndværk og ærlige aftaler. Vi servicerer og reparerer alle typer biler – altid med omhu, præcision og det nyeste udstyr på markedet.
                  </p>
                  <p>
                    Når du vælger os, vælger du et værksted, hvor en aftale er en aftale, og hvor vi udelukkende bruger kvalitetsreservedele for at sikre holdbare og sikre løsninger.
                  </p>
                </div>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  Derfor vælger kunder Sakskær Auto i Varde:
                </h2>

                <div className="space-y-4 pt-1">
                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-slate-900">Professionel og erfaren mekaniker</h3>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed font-normal">
                      Vi har mange års erfaring og ved, hvad din bil har brug for. Uanset om det gælder service, fejlsøgning eller reparation, kan du forvente faglig rådgivning og solidt håndværk.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-slate-900">Det nyeste udstyr</h3>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed font-normal">
                      Moderne biler kræver moderne udstyr. Vi investerer løbende i det nyeste diagnose- og værkstedsudstyr, så vi hurtigt og præcist kan finde og løse problemer.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-slate-900">Aftaler der holder</h3>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed font-normal">
                      Hos Sakskær Auto får du ærlige svar og faste priser. Vi overholder altid det, vi aftaler – både med hensyn til tid og pris.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-slate-900">Fokus på godt håndværk og kvalitet</h3>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed font-normal">
                      Vi går aldrig på kompromis. Alle reparationer og services udføres med stor omhu – og vi bruger udelukkende kvalitetsreservedele, så du får en løsning, der holder i længden.
                    </p>
                  </div>
                </div>

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'service' ? (
              <div className="space-y-4 text-left">
                <span className="block text-xs md:text-sm font-extrabold uppercase tracking-wider text-[#0F3C5F]">
                  Din bil fortjener den bedste pleje
                </span>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight text-[#0A0E1A] leading-[1.1]">
                  Service efter bogen hos Sakskær Auto – Bevar din fabriksgaranti
                </h1>

                <div className="space-y-4 text-base md:text-[17px] leading-relaxed text-[#525B68] font-normal">
                  <p>
                    Hos Sakskær Auto i Varde tilbyder vi professionel bilservice efter producentens 
                    anvisninger, så du bibeholder bilens fabriksgaranti – præcis som hvis du brugte mærkeværkstedet.
                  </p>
                  <p>
                    Vi ved, hvor vigtigt det er, at din bil er pålidelig og i god stand, uanset om du pendler hver dag, 
                    bruger bilen til familien eller bare skal være sikker på, at den starter hver gang. Derfor går vi aldrig på 
                    kompromis med kvaliteten – hverken i arbejdet eller de dele, vi bruger.
                  </p>
                </div>

                <div className="pt-2 space-y-4">
                  <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-[#0A0E1A] leading-snug">
                    Fabriksgaranti? Intet problem
                  </h2>
                  <div className="space-y-4 text-base md:text-[17px] leading-relaxed text-[#525B68] font-normal">
                    <p>
                      Mange tror fejlagtigt, at man skal bruge et autoriseret mærkeværksted for at bevare garantien. 
                      Det passer ikke. Når du får lavet service efter bilfabrikantens forskrifter – som vi altid gør hos Sakskær Auto – forbliver din garanti gyldig.
                    </p>
                    <p>
                      Vi følger alle intervaller, bruger korrekte specifikationer og dokumenterer alt arbejdet grundigt ved at stemple 
                      bilens originale eller online servicebog. Det sikrer dig fuld dokumentation og tryghed, hvis du skal bruge garantien eller sælge bilen videre.
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <Link
                    to="/kontakt/"
                    className="inline-flex items-center justify-center bg-[#0E6BB8] hover:bg-[#0B5492] text-white font-bold text-base px-7 py-3.5 rounded-xs shadow-xs transition-colors duration-200 cursor-pointer"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'udskiftning-af-tandrem-og-taktkaede' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs font-black uppercase tracking-wider text-[#082845]">
                  Din bil fortjener den bedste pleje
                </p>

                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-[#082845] leading-[1.12]">
                  Udskiftning af tandrem og taktkæde i Varde – Tryghed og ekspertise hos Sakskær Auto
                </h1>

                <div className="space-y-4 text-[#525b68] text-base leading-relaxed">
                  <p>
                    Hos Sakskær Auto i Varde er vi specialister i udskiftning af tandremme og taktkæder på alle bilmærker. Uanset om din bil er udstyret med tandrem eller taktkæde, er det vigtigt at få tjekket og vedligeholdt motorens drivsystem i tide – før det ender i dyre motorreparationer.
                  </p>
                  <p>
                    Vi tilbyder professionel rådgivning og gennemsigtig service – så du ved præcis, hvornår og hvorfor det er tid til at skifte.
                  </p>
                </div>

                <div className="pt-2 space-y-4">
                  <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight leading-snug text-[#082845]">
                    Tandrem – Hvorfor skal den skiftes i tide?
                  </h2>
                  <div className="space-y-4 text-[#525b68] text-base leading-relaxed">
                    <p>
                      Tandremmen er en sliddel, som holder motorens bevægelige dele synkroniseret. Hvis den springer, kan det medføre alvorlige skader på motoren – i værste fald totalskade.
                    </p>
                    <p>
                      De fleste biler har et fast udskiftningsinterval, f.eks. hver 5-8 år eller efter et bestemt antal kilometer. Hos Sakskær Auto holder vi styr på dette for dig – så du ikke bliver overrasket.
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <Link
                    to="/kontakt/"
                    className="inline-flex items-center justify-center px-6 py-3.5 bg-[#0b63b6] hover:bg-[#084e91] text-white font-bold text-base rounded transition-colors duration-200 cursor-pointer shadow-none"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : cleanSlug === 'walnut-blaster' ? (
              <div className="space-y-4 text-left">
                <p className="text-xs sm:text-sm font-extrabold uppercase tracking-wider text-[#0e3f5c]">
                  DIN BIL FORTJENER DEN BEDSTE PLEJE
                </p>

                <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black uppercase tracking-tight text-[#080a0c] leading-[1.08]">
                  RENSNING AF INDSUGNING MED WALNUT BLASTER HOS SAKSKÆR AUTO I VARDE
                </h1>

                <p className="text-base sm:text-lg text-[#525b68] font-normal leading-relaxed">
                  Hos Sakskær Auto i Varde tilbyder vi professionel og effektiv rensning af indsugningssystemer med den avancerede Walnut Blaster-teknologi. Denne metode er ideel til at fjerne sodophobninger, som kan samle sig i indsugningskanaler og påvirke motorkraften.
                </p>

                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                  HVORFOR VÆLGE WALNUT BLASTER TIL INDSUGNINGSRENSNING?
                </h2>

                <p className="text-base sm:text-lg text-[#525b68] font-normal leading-relaxed">
                  Walnut Blaster er en skånsom, men kraftfuld rengøringsmetode, der bruger finvalsede nøddeskalsplinter til at fjerne ophobet sod og snavs. Denne teknik sikrer en dybdegående rengøring uden at skade motordelen eller indre dele, hvilket gør den til et sikkert og miljøvenligt valg.
                </p>

                <div className="pt-2">
                  <Link
                    to="/kontakt/"
                    className="bg-[#0e6cb8] hover:bg-[#0a528e] text-white font-bold text-base px-6 py-3.5 rounded-xs transition-colors duration-200 inline-block"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-4 text-left">
                <span className="text-xs font-extrabold uppercase tracking-wider text-[#046BB4] block">
                  {staticPage.topTag || "DIN BIL FORTJENER DEN BEDSTE PLEJE"}
                </span>
                
                <h1 className="text-2xl sm:text-3xl lg:text-[42px] font-black uppercase tracking-tight text-slate-900 leading-tight">
                  {mainHeading}
                </h1>

                {staticPage.heroSub && (
                  <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-line font-normal">
                    {staticPage.heroSub}
                  </p>
                )}

                {staticPage.listTitle && (
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight pt-2">
                    {staticPage.listTitle}
                  </h2>
                )}

                {staticPage.list && staticPage.list.length > 0 && (
                  <ul className="list-disc list-outside pl-5 space-y-2 text-slate-700 text-sm sm:text-base font-normal">
                    {staticPage.list.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                )}

                <div className="pt-3">
                  <Link
                    to="/kontakt/"
                    className="inline-block bg-[#046BB4] hover:bg-[#035590] text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-sm transition-colors duration-200 shadow-sm uppercase tracking-wide"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* Main Page Content - Design matching reference image */}
      {(() => {
        const designData = SUBPAGE_DESIGN_DATA[cleanSlug] || SUBPAGE_DESIGN_DATA['service']
        return (
          <section className="bg-zinc-950 text-white pt-16 md:pt-24 pb-0 font-sans antialiased">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 space-y-12 lg:space-y-16">
              
              {/* Top Header Block (hidden for daek-og-faelge, kundebil, reparation, and walnut-blaster) */}
              {cleanSlug !== 'daek-og-faelge' && cleanSlug !== 'kundebil' && cleanSlug !== 'reparation' && cleanSlug !== 'walnut-blaster' && (
                <div className={`mx-auto text-center space-y-6 md:space-y-8 ${cleanSlug === 'gearkasse-service' ? 'max-w-3xl' : 'max-w-3xl'}`}>
                  
                  {/* Section Tagline */}
                  <p className={`text-[11px] uppercase tracking-widest ${cleanSlug === 'gearkasse-service' ? 'font-extrabold text-neutral-300' : 'md:text-xs font-black tracking-[0.25em] text-white'}`}>
                    {designData.tagline}
                  </p>

                  {/* Sub-block 1 */}
                  <div className="space-y-3">
                    <h2 className={`font-black uppercase tracking-tight text-white leading-tight ${cleanSlug === 'gearkasse-service' ? 'text-2xl md:text-3xl lg:text-[30px]' : 'text-xl sm:text-2xl lg:text-[28px]'}`}>
                      {designData.headerBlock.block1Title}
                    </h2>
                    {designData.headerBlock.block1List && designData.headerBlock.block1List.length > 0 ? (
                      <ul className={cleanSlug === 'gearkasse-service' 
                        ? 'max-w-2xl mx-auto text-center font-normal space-y-3 text-sm md:text-base text-neutral-300 pt-2 list-none pl-0' 
                        : 'list-disc list-outside pl-5 max-w-2xl mx-auto text-left font-normal space-y-2 text-xs md:text-sm text-zinc-400 leading-relaxed'
                      }>
                        {designData.headerBlock.block1List.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`max-w-2xl mx-auto whitespace-pre-line ${cleanSlug === 'gearkasse-service' ? 'text-sm md:text-base text-neutral-300 leading-relaxed' : 'text-xs md:text-sm text-zinc-400 leading-relaxed'}`}>
                        {designData.headerBlock.block1Text}
                      </p>
                    )}
                  </div>

                  {/* Sub-block 2 */}
                  {designData.headerBlock.block2Title && (
                    <div className="space-y-3 pt-2">
                      <h3 className={`font-black uppercase tracking-tight text-white ${cleanSlug === 'gearkasse-service' ? 'text-2xl md:text-3xl lg:text-[30px]' : 'text-xl sm:text-2xl lg:text-[28px]'}`}>
                        {designData.headerBlock.block2Title}
                      </h3>
                      <p className={`max-w-2xl mx-auto whitespace-pre-line ${cleanSlug === 'gearkasse-service' ? 'text-sm md:text-base text-neutral-300 leading-relaxed' : 'text-xs md:text-sm text-zinc-400 leading-relaxed'}`}>
                        {designData.headerBlock.block2Text}
                      </p>
                    </div>
                  )}

                </div>
              )}

              {/* Feature Split Card Component */}
              <div className={`bg-white text-black rounded-xl overflow-hidden shadow-2xl ${cleanSlug === 'gearkasse-service' || cleanSlug === 'service' || cleanSlug === 'udskiftning-af-tandrem-og-taktkaede' || cleanSlug === 'walnut-blaster' || !designData.splitCard.image ? 'block w-full' : 'grid grid-cols-1 md:grid-cols-2'}`}>
                
                {/* Left: Image Box */}
                {cleanSlug !== 'gearkasse-service' && cleanSlug !== 'service' && cleanSlug !== 'udskiftning-af-tandrem-og-taktkaede' && cleanSlug !== 'walnut-blaster' && designData.splitCard.image && (
                  <div className="relative min-h-[300px] md:min-h-0 w-full h-full bg-zinc-800">
                    <img 
                      src={getOptimizedImg(designData.splitCard.image, 1000)} 
                      alt={designData.splitCard.block1Title} 
                      className="w-full h-full object-cover md:absolute md:inset-0" 
                    />
                  </div>
                )}

                {/* Right: Content Area */}
                {cleanSlug === 'walnut-blaster' ? (
                  <div className="p-8 sm:p-10 md:p-12 space-y-8 text-neutral-900 bg-white rounded-xl">
                    {/* First Block: Benefits */}
                    <div>
                      <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight mb-4">
                        FORDELE VED RENSNING AF INDSUGNING MED WALNUT BLASTER
                      </h2>
                      
                      <ul className="list-disc pl-5 space-y-2 text-sm md:text-base leading-relaxed text-gray-600">
                        <li>
                          <span className="font-medium text-gray-800">Forbedret motoreffektiveret:</span>{" "}
                          Fjernelse af ophobet sod og snavs betyder, at luften kan cirkulere frit, hvilket forbedrer motorydelsen.
                        </li>
                        <li>
                          <span className="font-medium text-gray-800">Øget brændstoføkonomi:</span>{" "}
                          En ren indsugning hjælper med at optimere brændstofforbruget, hvilket sparer dig penge.
                        </li>
                        <li>
                          <span className="font-medium text-gray-800">Reduktion af emissioner:</span>{" "}
                          En renere indsugning bidrager til lavere udledning af skadelige stoffer.
                        </li>
                        <li>
                          <span className="font-medium text-gray-800">Forebyggelse af motorskade:</span>{" "}
                          Regelmæssig rengøring minimerer risikoen for motorskader og forlænger levetiden på dit køretøj.
                        </li>
                      </ul>
                    </div>

                    {/* Second Block: When is it necessary */}
                    <div>
                      <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight mb-4">
                        HVORNÅR ER DET NØDVENDIGT AT FÅ RENSET INDSUGNINGEN?
                      </h2>
                      
                      <p className="text-sm md:text-base leading-relaxed text-gray-600">
                        Hvis du oplever problemer som kraftig røgudvikling, nedsat kraft, eller hvis motorydelsen føles hæmmet, kan det være tegn på, at indsugningen trænger til rengøring. En rutinemæssig indsugningsrensning med Walnut Blaster anbefales også for at opretholde din bils optimale funktion.
                      </p>
                    </div>

                    {/* Primary Call to Action */}
                    <div>
                      <Link 
                        to="/kontakt/" 
                        className="inline-block bg-[#0f62fe] hover:bg-[#0043ce] text-white font-medium text-sm px-6 py-3 rounded-sm shadow-xs transition-colors duration-150"
                      >
                        Indhent et tilbud
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className={`${cleanSlug === 'gearkasse-service' || cleanSlug === 'service' || cleanSlug === 'udskiftning-af-tandrem-og-taktkaede' || !designData.splitCard.image ? 'p-8 sm:p-10 md:p-12 space-y-6 text-neutral-900' : 'p-8 md:p-12 flex flex-col justify-center space-y-6'}`}>
                    
                    {/* Content Block 1 */}
                    <div className="space-y-3">
                      <h3 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight">
                        {designData.splitCard.block1Title}
                      </h3>
                      {designData.splitCard.block1List && designData.splitCard.block1List.length > 0 && (
                        <ul className="list-disc list-outside pl-5 space-y-2 text-sm md:text-base text-neutral-600 leading-relaxed font-normal">
                          {designData.splitCard.block1List.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      )}
                      {designData.splitCard.block1Text && (
                        <p className={`leading-relaxed whitespace-pre-line font-normal ${cleanSlug === 'gearkasse-service' || cleanSlug === 'service' || cleanSlug === 'udskiftning-af-tandrem-og-taktkaede' ? 'text-sm md:text-base text-neutral-600' : 'text-xs md:text-sm text-zinc-600'}`}>
                          {designData.splitCard.block1Text}
                        </p>
                      )}
                    </div>

                    {/* Content Block 2 */}
                    {designData.splitCard.block2Title && (
                      <div className="space-y-3 pt-2">
                        <h3 className={cleanSlug === 'gearkasse-service'
                          ? 'leading-relaxed whitespace-pre-line font-normal text-sm md:text-base text-neutral-600'
                          : 'text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-[#080a0c] leading-tight'
                        }>
                          {designData.splitCard.block2Title}
                        </h3>
                        <p className={`leading-relaxed whitespace-pre-line font-normal ${cleanSlug === 'gearkasse-service' || cleanSlug === 'reparation' || cleanSlug === 'service' ? 'text-sm md:text-base text-neutral-600' : 'text-xs md:text-sm text-zinc-600'}`}>
                          {designData.splitCard.block2Text}
                        </p>
                      </div>
                    )}

                    {/* Call to Action */}
                    <div className="pt-4">
                      {designData.splitCard.buttonLink.startsWith('tel:') ? (
                        <a
                          href={designData.splitCard.buttonLink}
                          className="inline-flex items-center justify-center bg-[#0b63ac] hover:bg-[#084e88] text-white font-semibold text-base px-6 py-3.5 rounded-none transition-colors duration-150 uppercase tracking-wide"
                        >
                          {designData.splitCard.buttonText}
                        </a>
                      ) : (
                        <Link
                          to={designData.splitCard.buttonLink}
                          className="inline-flex items-center justify-center bg-[#0b63ac] hover:bg-[#084e88] text-white font-semibold text-base px-6 py-3.5 rounded-none transition-colors duration-150 uppercase tracking-wide"
                        >
                          {designData.splitCard.buttonText}
                        </Link>
                      )}
                    </div>

                  </div>
                )}
              </div>

              {/* Extra page-specific details (Lightboxes) if available */}

            </div>
          </section>
        )
      })()}
    </div>
  )
}
